/// <reference path="../../exported/ajaxUtils.d.ts" />

import React = require("react");
import * as AutoSuggest from "react-autosuggest";
import "./CompanySearchDefinitionComponent.scss";
import {defaultTheme} from "@vebox/etui-themes";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../index";

const CREATE_NEW_OPTION_GUID = "0f4245d8-8390-11e9-a668-941882820f48";
const NO_OPTION_FOUND_GUID = "498c312c-85fc-11e9-a07b-941882820f48";
const SUGGESTION_URL = "hierarchies/company/suggestions";

export interface CompanySuggestion {
    guid: string;
    name: string;
    code: string;
    vat: string;
    parentCompany?: CompanySuggestion;
}

interface Props {
    title: string;
    allowCompanyCreation: boolean;
    companyNameTitle?: string;
    companyCodeTitle?: string;
    companyVatTitle?: string;
    createNewCompanyTitle?: string;
    hierarchyGuid: string;
    hierarchyLevelIndex: number;
    parentCompanyTitle?: string;
    noElementsFoundText: string;
    onChange: (suggestion: CompanySuggestion) => void;
}

interface State {
    companyName: string;
    companyCode: string;
    companyVat: string;
    createNew: boolean;
    suggestions: CompanySuggestion[];
    selectedSuggestion: CompanySuggestion;
    selectedSlaveSuggestion: CompanySuggestion;
    value: string;
}

export class CompanySearchDefinitionComponent extends React.Component<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            value: "",
            suggestions: [],
            selectedSuggestion: null,
            selectedSlaveSuggestion: null,
            createNew: false,
            companyCode: "",
            companyName: "",
            companyVat: "",
        };
        this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
        this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
        this.getSuggestionValue = this.getSuggestionValue.bind(this);
        this.renderSuggestion = this.renderSuggestion.bind(this);
        this.onSuggestionClick = this.onSuggestionClick.bind(this);
        this.undoSelection = this.undoSelection.bind(this);
        this.onCompanyNameChange = this.onCompanyNameChange.bind(this);
        this.onCompanyCodeChange = this.onCompanyCodeChange.bind(this);
        this.onCompanyVatChange = this.onCompanyVatChange.bind(this);
        this.onSlaveSuggestChange = this.onSlaveSuggestChange.bind(this);
    }

    public reset() {
        this.setState(
            {
                value: "",
                suggestions: [],
                selectedSuggestion: null,
                createNew: false,
                companyCode: "",
                companyName: "",
                companyVat: "",
            },
        );
    }

    public render() {
        // Auto suggest will pass through all these props to the input.
        const onChangeFunction = () => {
            //console.log("Suggest input changed");
        };
        let content = null;
        if (this.state.createNew) {
            content = this.renderSelectedBox(this.props.createNewCompanyTitle);
        } else {
            if (this.state.selectedSuggestion != null) {
                content = this.renderSelectedBox(this.state.selectedSuggestion.name);
            } else {
                const inputProps = {
                    value: this.state.value,
                    onChange: onChangeFunction
                };
                content = (
                    <AutoSuggest
                        suggestions={this.state.suggestions}
                        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                        getSuggestionValue={this.getSuggestionValue}
                        renderSuggestion={this.renderSuggestion}
                        inputProps={inputProps}
                    />
                );
            }
        }

        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div>
                    <div className="bottomAlignDiv"/>
                    <div className="rowAligner">{this.props.title}</div>
                    {content}
                </div>
            </ThemeProvider>
        );
    }

    /**
     *  The request must return a list of JSONCompany
     */
    private loadSuggestions(value: string) {
        this.setState({value, selectedSuggestion: null, createNew: false});
        Ajax.vbxAjax({
            url: SUGGESTION_URL,
            data: {value, hierarchyGuid: this.props.hierarchyGuid, limit: 10},
            type: "GET",
            cache: false,
            success: (response: CompanySuggestion[]) => {
                if (response != null) {
                    if (response.length === 0) {
                        if (this.props.allowCompanyCreation) {
                            this.setState({
                                    suggestions: [{
                                        guid: CREATE_NEW_OPTION_GUID,
                                        name: this.props.createNewCompanyTitle,
                                        code: null,
                                        vat: null,
                                    }],
                                },
                            );
                        } else {
                            this.setState({
                                    suggestions: [{
                                        guid: NO_OPTION_FOUND_GUID,
                                        name: this.props.noElementsFoundText,
                                        code: null,
                                        vat: null,
                                    }],
                                },
                            );
                        }
                    } else {
                        this.setState({
                                suggestions: response,
                            },
                        );
                    }
                }
            },
        });
    }

    // Autosuggest will call this function every time you need to update suggestions.
    private onSuggestionsFetchRequested({value}) {
        this.loadSuggestions(value);
    }

    private onSuggestionsClearRequested() {
        this.setState({
            suggestions: [],
            value: this.state.value.length === 1 ? "" : this.state.value,
        });
    }

    private getSuggestionValue(suggestion) {
        return suggestion.name;
    }

    private renderSuggestion(suggestion) {
        let classNames = "list-suggestion";
        if (suggestion.guid === CREATE_NEW_OPTION_GUID) {
            classNames += " list-suggestion-create-new";
        }
        const code = (suggestion.code || "");
        const vat = suggestion.vat || "";
        const codeAndVat = code + (code.length > 0 && vat.length > 0 ? " - " : "") + vat;
        return (
            <div
                className={classNames}
                onClick={this.onSuggestionClick}
                data-id={suggestion.guid}
                data-value-id={suggestion.name}
            >
                {codeAndVat && <span>({codeAndVat}) </span>}
                <span>{suggestion.name}</span>
                {suggestion?.parentCompany !== null && (
                    <span> [{suggestion?.parentCompany?.name}]</span>
                )}
            </div>
        );
    }

    private findSelectedSuggestion(suggestionGuid: string) {
        for (const suggestion of this.state.suggestions) {
            if (suggestion.guid === suggestionGuid) {
                return suggestion;
            }
        }
        return null;
    }

    private renderSelectedBox(text: string) {
        let createCompanyForm = null;
        let selectedItemClassname = "list-suggestion__selected-item-text";
        let parentCompanySuggest = null;
        if (this.props.allowCompanyCreation) {
            parentCompanySuggest = (
                <div className={"form-element inside-company-search-definition"}>
                    <CompanySearchDefinitionComponent
                        title={this.props.parentCompanyTitle}
                        allowCompanyCreation={false}
                        onChange={this.onSlaveSuggestChange}
                        hierarchyGuid={this.props.hierarchyGuid}
                        hierarchyLevelIndex={this.props.hierarchyLevelIndex ? this.props.hierarchyLevelIndex + 1 : 1}
                        noElementsFoundText={this.props.noElementsFoundText}
                    />
                </div>
            );
        }

        if (this.state.createNew) {
            createCompanyForm = (
                <div className={"company-search-definition-component__new-element-form-container"}>
                    <div className={"form-element"}>
                        <div className="mandatoryFieldIndicator"/>
                        <p className={"form-element_title"}>{this.props.companyNameTitle}</p>
                        <input
                            type={"text"}
                            className={"form-element__input"}
                            onChange={this.onCompanyNameChange}
                            value={this.state.companyName}
                        />
                    </div>
                    <div className={"form-element"}>
                        <p className={"form-element_title"}>{this.props.companyCodeTitle}</p>
                        <input
                            type={"text"}
                            className={"form-element__input"}
                            onChange={this.onCompanyCodeChange}
                            value={this.state.companyCode}
                        />
                    </div>
                    <div className={"form-element"}>
                        <p className={"form-element_title"}>{this.props.companyVatTitle}</p>
                        <input
                            type={"text"}
                            className={"form-element__input"}
                            onChange={this.onCompanyVatChange}
                            value={this.state.companyVat}
                        />
                    </div>
                    {parentCompanySuggest}
                </div>
            );
            selectedItemClassname += " list-suggestion__selected-item-text-new italic ";
        }
        return (
            <div className={"list-suggestion__selected-item-container"}>
                <div className={"selected-item"}>
                    <p className={selectedItemClassname}>{text}</p>
                    <div className={"list-suggestion__undo-selection-button"} onClick={this.undoSelection}>X</div>
                </div>
                {createCompanyForm}
            </div>
        );
    }

    private undoSelection() {
        this.setState({
            value: "",
            selectedSuggestion: null,
            createNew: false,
            companyName: "",
            companyCode: "",
            companyVat: "",
        });
        this.props.onChange(null);
    }

    private onSuggestionClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        const name = e.currentTarget.dataset.valueId;
        const guid = e.currentTarget.dataset.id;
        if (guid === CREATE_NEW_OPTION_GUID) {
            this.setState({value: "", selectedSuggestion: null, createNew: true});
        } else if (guid !== NO_OPTION_FOUND_GUID) {
            const suggestion = this.findSelectedSuggestion(guid);
            this.setState({value: name, selectedSuggestion: suggestion, createNew: false});
            this.props.onChange(suggestion);
        }
    }

    private onSlaveSuggestChange(suggestion: CompanySuggestion) {
        this.setState({selectedSlaveSuggestion: suggestion});
        this.props.onChange({
            guid: null,
            name: this.state.companyName,
            code: this.state.companyCode,
            vat: this.state.companyVat,
            parentCompany: suggestion,
        });
    }

    private onCompanyNameChange(e: React.ChangeEvent<HTMLInputElement>) {
        this.setState({companyName: e.target.value});
        this.props.onChange({
            guid: null,
            name: e.target.value,
            code: this.state.companyCode,
            vat: this.state.companyVat,
            parentCompany: this.state.selectedSlaveSuggestion,
        });
    }

    private onCompanyCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
        this.setState({companyCode: e.target.value});
        this.props.onChange({
            guid: null,
            name: this.state.companyName,
            code: e.target.value,
            vat: this.state.companyVat,
            parentCompany: this.state.selectedSlaveSuggestion,
        });
    }

    private onCompanyVatChange(e: React.ChangeEvent<HTMLInputElement>) {
        this.setState({companyVat: e.target.value});
        this.props.onChange({
            guid: null,
            name: this.state.companyName,
            code: this.state.companyCode,
            vat: e.target.value,
            parentCompany: this.state.selectedSlaveSuggestion,
        });
    }
}
